import { Inject, Injectable } from '@angular/core';
import {
  Auth,
  EmailAuthProvider,
  User,
  deleteUser,
  reauthenticateWithCredential,
  updatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import {
  Firestore,
  collection,
  deleteDoc,
  doc,
  getDocs,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { IClass } from '../models/class.model';
import { IUserClass } from '../models/userClass.model';
import { NotSignedInError } from '../errors/NotSignedInError';
import { SameEmailError } from '../errors/SameEmailError';
import { AccountPartiallyDeletedError } from '../errors/AccountPartiallyDeletedError';
import { FIREBASE_AUTH, FIRESTORE } from '../firebase.providers';

/** Self-service account management for the logged-in member (/mijn-gegevens).
 *
 *  Nothing here streams, so no observeInZone: every method is a one-shot await. It needs
 *  auth.currentUser synchronously, to build the reauthentication credential from the
 *  address Firebase Auth holds right now.
 *
 *  Every operation re-authenticates first. Firebase requires a recent login for these
 *  three, and the password prompt is also what proves the person at the keyboard is the
 *  account owner. */
@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private static readonly WRITES_PER_BATCH = 400; // Firestore caps a batch at 500 ops; 2 deletes per class.

  constructor(
    @Inject(FIREBASE_AUTH) private auth: Auth,
    @Inject(FIRESTORE) private db: Firestore,
  ) {}

  /** Sends a confirmation link to the NEW address. The address only changes once that
   *  link is opened, which lands on /auth/email/action?mode=verifyAndChangeEmail.
   *  Applying the code revokes the refresh tokens, so the member is signed out and logs
   *  back in with the new address. users/{uid}.email catches up in AuthService on that
   *  next sign-in: the link is opened from the mail client with no session, so the
   *  confirm page cannot write Firestore. */
  async changeEmail(newEmail: string, currentPassword: string): Promise<void> {
    const user = await this.reauthenticate(currentPassword);
    // Firebase answers "email-already-in-use" when the address is the member's own,
    // which is true and useless: the account it collides with is theirs.
    if ((user.email ?? '').toLowerCase() === newEmail.toLowerCase()) {
      throw new SameEmailError();
    }
    await verifyBeforeUpdateEmail(user, newEmail);
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    const user = await this.reauthenticate(currentPassword);
    await updatePassword(user, newPassword);
  }

  /** Erases the member: reservation mirrors, user doc, Auth account. In that order,
   *  because every Firestore write needs request.auth.uid == userId, so nothing is
   *  reachable once the Auth account is gone, and because Firestore does not cascade,
   *  so users/{uid}/classes must be emptied before the user doc goes.
   *
   *  Releasing the mirrors and deleting the user doc are idempotent: a failure halfway
   *  is fixed by pressing the button again. The one unrecoverable window is a failure of
   *  the very last call, which leaves an Auth account with no user doc. That session
   *  renders as logged out and can do nothing, and only an admin can clear it from the
   *  Firebase console, hence AccountPartiallyDeletedError and its Dutch message. */
  async deleteAccount(currentPassword: string): Promise<void> {
    const user = await this.reauthenticate(currentPassword);
    const uid = user.uid;

    const mirrors = await getDocs(collection(this.db, `users/${uid}/classes`));
    const pastClassIds: string[] = [];

    for (const mirror of mirrors.docs) {
      const classId = mirror.id;
      const reservations = (mirror.data() as IUserClass).reservations ?? 0;
      const released = await this.releaseClass(uid, classId, reservations);
      if (!released) {
        pastClassIds.push(classId);
      }
    }
    await this.deleteMirrors(uid, pastClassIds);

    await deleteDoc(doc(this.db, `users/${uid}`));

    try {
      await deleteUser(user);
    } catch {
      // A stale token is the likely cause; a fresh reauthentication fixes it. The user
      // doc is already gone at this point, so there is nothing to roll back to.
      try {
        const fresh = await this.reauthenticate(currentPassword);
        await deleteUser(fresh);
      } catch {
        throw new AccountPartiallyDeletedError();
      }
    }
  }

  /** Frees the member's seats in one transaction and deletes both mirror documents.
   *  Returns false when the class already happened, so the caller deletes those mirrors
   *  without touching the counter: currentReservations on a past class is an attendance
   *  figure, not personal data, and rewriting it would falsify the admin history. The
   *  invariant counter == sum(mirrors) therefore stops holding for past classes.
   *
   *  Not a loop over ReservationService.removeReservation: that frees one seat per call,
   *  leaves the mirrors behind at reservations: 0, and throws EmailNotVerifiedError when
   *  the user doc is missing or unverified, which would trap exactly the members who
   *  most want their data gone. */
  private async releaseClass(
    uid: string,
    classId: string,
    reservations: number,
  ): Promise<boolean> {
    const classRef = doc(this.db, `classes/${classId}`);

    return await runTransaction(this.db, async (transaction) => {
      const classDoc = await transaction.get(classRef);
      const iClass = classDoc.data() as IClass | undefined;
      // The class doc holds the authoritative date; mirrors written before dateEpoch was
      // introduced do not carry one.
      const upcoming = !!iClass && iClass.dateEpoch > Date.now();
      if (!upcoming) {
        return false;
      }
      if (reservations > 0) {
        // The rules allow a member to change currentReservations and nothing else.
        transaction.update(classRef, {
          currentReservations: Math.max(
            0,
            (iClass!.currentReservations ?? 0) - reservations,
          ),
        });
      }
      transaction.delete(doc(this.db, `classes/${classId}/users/${uid}`));
      transaction.delete(doc(this.db, `users/${uid}/classes/${classId}`));
      return true;
    });
  }

  private async deleteMirrors(uid: string, classIds: string[]): Promise<void> {
    for (let i = 0; i < classIds.length; i += AccountService.WRITES_PER_BATCH) {
      const batch = writeBatch(this.db);
      for (const classId of classIds.slice(
        i,
        i + AccountService.WRITES_PER_BATCH,
      )) {
        batch.delete(doc(this.db, `classes/${classId}/users/${uid}`));
        batch.delete(doc(this.db, `users/${uid}/classes/${classId}`));
      }
      await batch.commit();
    }
  }

  private async reauthenticate(currentPassword: string): Promise<User> {
    const user = this.auth.currentUser;
    if (!user?.email) {
      throw new NotSignedInError();
    }
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword,
    );
    await reauthenticateWithCredential(user, credential);
    return user;
  }
}
